import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { FlexBox } from '@dixa/lib-shared-habla';
import identity from 'lodash/identity';
import { useMemo } from 'react';
import { getConversationEventGroups, } from 'utils/conversation/groupConversationEvents';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { ConversationAudioEventGroup } from './components/groups/audioEventGroup/conversationAudioEventGroup';
import { ConversationEmailEventGroup } from './components/groups/emailEventGroup/conversationEmailEventGroup';
import { ConversationInstagramStoryEventGroup } from './components/groups/instagramStoryEventGroup/conversationInstagramStoryEventGroup';
import { ConversationInternalNoteEventGroup } from './components/groups/internalNoteEventGroup/conversationInternalNoteEventGroup';
import { ConversationTextualEventGroup } from './components/groups/textualEventGroup/conversationTextualEventGroup';
/** Represents the component for displaying conversation event groups. */
export const ConversationEventGroups = ({ className, emptyMessage, groupsConfig, mainConversation, advancedFilter = identity, }) => {
    const { enableCallRecordings } = useFeatureFlag(['enable-call-recordings']);
    const eventGroups = useMemo(() => {
        const eventGroupTypes = groupsConfig.reduce((groupTypes, groupConfig) => [
            ...groupTypes,
            ...(typeof groupConfig === 'object'
                ? Object.keys(groupConfig)
                : [groupConfig]),
        ], []);
        return advancedFilter(getConversationEventGroups(mainConversation, eventGroupTypes));
    }, [groupsConfig, advancedFilter, mainConversation]);
    const eventGroupComponents = useMemo(() => {
        const { authors } = mainConversation;
        return eventGroups
            .map((eventGroup, index) => {
            switch (eventGroup.type) {
                // TODO: [TEAMS-10805] Implement the status event group components
                case 'group:conversation:agent_assigned':
                    return _jsx(_Fragment, { children: "conversation agent assigned" });
                case 'group:conversation:agent_unassigned':
                    return _jsx(_Fragment, { children: "conversation agent unassigned" });
                case 'group:conversation:closed':
                    return _jsx(_Fragment, { children: "conversation closed" });
                case 'group:conversation:started':
                    return _jsx(_Fragment, { children: "conversation started" });
                case 'group:conversation:conversational_assistant':
                    return _jsx(_Fragment, { children: "conversation conversational assistant" });
                case 'group:conversation:rating':
                    return _jsx(_Fragment, { children: "conversation rating" });
                case 'group:conversation:transfer':
                    return _jsx(_Fragment, { children: "conversation transfer" });
                case 'group:conversation:wrapup_message':
                    return (_jsx(ConversationInternalNoteEventGroup, Object.assign({ author: authors[eventGroup.events[0].author_id], events: eventGroup.events }, getConversationEventGroupConfig(groupsConfig, eventGroup)), index));
                case 'group:instagram_story':
                    return (_jsx(ConversationInstagramStoryEventGroup, Object.assign({ attributes: mainConversation.attributes, author: authors[eventGroup.events[0].author_id], events: eventGroup.events }, getConversationEventGroupConfig(groupsConfig, eventGroup)), index));
                case 'group:email':
                case 'group:contact_form':
                    return (_jsx(ConversationEmailEventGroup, Object.assign({ author: authors[eventGroup.events[0].author_id], events: eventGroup.events }, getConversationEventGroupConfig(groupsConfig, eventGroup)), index));
                case 'group:facebook_messenger':
                case 'group:messenger':
                case 'group:whats_app':
                case 'group:widget_chat':
                case 'group:sms':
                case 'group:twitter':
                    return (_jsx(ConversationTextualEventGroup, Object.assign({ author: authors[eventGroup.events[0].author_id], events: eventGroup.events }, getConversationEventGroupConfig(groupsConfig, eventGroup)), index));
                case 'group:pstn_phone':
                case 'group:voicemail': {
                    const hasRecordingUrl = eventGroup.type === 'group:pstn_phone' &&
                        eventGroup.events.some((event) => event.recording_url);
                    const hasVoicemailUrl = eventGroup.type === 'group:voicemail' &&
                        eventGroup.events.some((event) => event.voicemail_url);
                    if ((hasRecordingUrl && enableCallRecordings) || hasVoicemailUrl) {
                        return (_jsx(ConversationAudioEventGroup, Object.assign({ author: authors[eventGroup.events[0].author_id], events: eventGroup.events }, getConversationEventGroupConfig(groupsConfig, eventGroup)), index));
                    }
                    break;
                }
            }
        })
            .filter(Boolean);
    }, [enableCallRecordings, eventGroups, groupsConfig, mainConversation]);
    return (_jsx("div", Object.assign({ className: className }, { children: _jsx(FlexBox, Object.assign({ flexDirection: "column", gap: "300" }, { children: eventGroupComponents.length > 0 ? eventGroupComponents : emptyMessage })) })));
};
/**
 * Gets the configuration of the props for the event group related component.
 *
 * @param groupsConfig The event groups configuration.
 * @param eventGroup The event group to get the configuration for.
 */
function getConversationEventGroupConfig(groupsConfig, eventGroup) {
    const eventGroupConfig = groupsConfig.find((config) => typeof config === 'object' && config[eventGroup.type]);
    return eventGroupConfig && eventGroupConfig[eventGroup.type];
}
